import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { SpreadSheetService } from "../s25-spreadsheet/spreadsheet.service";
import { RowData } from "../s25-spreadsheet/types/sheet.js.types.expanded";
import { ProductLicenseStore } from "../../stores/product.license.store";
import { Conflicts } from "./import.service";
import { ImportTelemetry } from "./import.telemetry";
import type { TelemetrySession } from "../../services/telemetry/telemetry.session";

export interface ImportType {
    name: string;
    value: "events" | "locations" | "resources" | "organizations" | "contacts" | "masterDefinitions" | "budgets";
    completeText: string;
    shown: boolean;
    init: boolean;
    errors: string[];
    successMessage: string;
    conflicts?: Conflicts;
    onComplete?: (rows: RowData[]) => Promise<void>;
}

@TypeManagerDecorator("s25-ng-import")
@Component({
    selector: "s25-ng-import",
    template: `
        @if (init) {
            <label
                >Select Import Type&nbsp;
                <select
                    class="cn-form__control c-margin-bottom--single"
                    [(ngModel)]="selectedType"
                    (ngModelChange)="onSelectedTypeChange($event)"
                >
                    @for (type of visibleImportTypes; track type.value) {
                        <option [ngValue]="type">
                            {{ type.name }}
                        </option>
                    }
                </select>
            </label>

            @for (type of visibleImportTypes; track type.value) {
                @if (type.init) {
                    <div [hidden]="!type.shown">
                        @if (type.value === "events") {
                            <s25-ng-import-events [type]="type" [parentCd]="cd"></s25-ng-import-events>

                            <div class="c-margin-bottom--single c-margin-top--single">
                                <details class="c-margin-bottom--single">
                                    <summary
                                        class="c-margin-bottom--single"
                                        style="cursor: pointer; font-weight: bold;"
                                    >
                                        Event Import Instructions
                                    </summary>
                                    <div class="import-instructions c-margin-left--single c-margin-top--single">
                                        <div class="c-margin-bottom--single">
                                            <strong>Required Columns:</strong>
                                            <ul class="c-margin-left--single c-margin-top--half">
                                                <li>
                                                    <code>EVENT_ID</code> (string) - Unique identifier for the event
                                                </li>
                                                <li><code>EVENT_NAME</code> (string) - Name of the event</li>
                                                <li><code>EVENT_TYPE</code> (string|number) - Event type name or ID</li>
                                                <li>
                                                    <code>SCHEDULER</code> (string|number) - Contact email or contact ID
                                                </li>
                                                <li>
                                                    <code>REQUESTOR</code> (string|number) - Contact email or contact ID
                                                </li>
                                                <li>
                                                    <code>PROFILE_ID</code> (string) - Unique identifier for the event
                                                    profile
                                                </li>
                                                <li>
                                                    <code>FIRST_OCCURRENCE_DATE</code> (string) - ISO date format
                                                    (YYYY-MM-DD)
                                                </li>
                                                <li>
                                                    <code>START_TIME</code> (string) - ISO datetime format
                                                    (YYYY-MM-DDTHH:mm:ss)
                                                </li>
                                                <li>
                                                    <code>END_TIME</code> (string) - ISO datetime format
                                                    (YYYY-MM-DDTHH:mm:ss)
                                                </li>
                                            </ul>
                                        </div>
                                        <div class="c-margin-bottom--single">
                                            <strong>Optional Columns:</strong>
                                            <ul class="c-margin-left--single c-margin-top--half">
                                                <li><code>EVENT_TITLE</code> (string)</li>
                                                <li><code>DESCRIPTION</code> (string)</li>
                                                <li><code>INTERNAL_NOTES</code> (string)</li>
                                                <li>
                                                    <code>PRIMARY_ORGANIZATION</code> (string|number) - Organization
                                                    name or ID
                                                </li>
                                                <li><code>REG_HEADCOUNT</code> (number)</li>
                                                <li><code>EXP_HEADCOUNT</code> (number)</li>
                                                <li>
                                                    <code>CATEGORIES</code> (string) - Pipe-separated category names or
                                                    IDs (e.g., "Category1|Category2")
                                                </li>
                                                <li>
                                                    <code>INSTRUCTOR</code> (string|number) - Contact email or contact
                                                    ID
                                                </li>
                                                <li><code>PROFILE_NAME</code> (string)</li>
                                                <li>
                                                    <code>PRE_EVENT_TIME</code> (string|number) - Time string (HH:mm:ss)
                                                    or minutes
                                                </li>
                                                <li>
                                                    <code>POST_EVENT_TIME</code> (string|number) - Time string
                                                    (HH:mm:ss) or minutes
                                                </li>
                                                <li>
                                                    <code>REPEATING_RULE</code> (string) - Recurrence pattern code (or
                                                    use ADHOC_DATES instead)
                                                </li>
                                                <li>
                                                    <code>ADHOC_DATES</code> (string) - Pipe-separated ISO dates
                                                    (alternative to REPEATING_RULE, e.g., "2024-01-15|2024-01-22")
                                                </li>
                                                <li>
                                                    <code>LOCATIONS</code> (string) - Pipe-separated location names or
                                                    IDs. Append <code>:shared</code> for shared locations (optional;
                                                    omitted locations default to unshared)
                                                </li>
                                                <li>
                                                    <code>RESOURCES</code> (string) - Pipe-separated resource names or
                                                    IDs, append <code>:quantity</code> (e.g.,
                                                    "Projector:2|Microphone:1")
                                                </li>
                                                <li>
                                                    <code>BOUND_PROFILE_ID</code> (string) - Pipe-separated profile IDs
                                                </li>
                                                <li>
                                                    <code>RELATED_EVENT_ID</code> (string) - Pipe-separated event IDs
                                                </li>
                                                <li>
                                                    <code>CUSTOM_ATTRIBUTES</code> (string) - Pipe-separated, format:
                                                    <code>name:value</code> (e.g.,
                                                    "Attribute1:Value1|Attribute2:Value2")
                                                </li>
                                            </ul>
                                        </div>
                                        <div class="c-margin-bottom--single">
                                            <strong>Notes:</strong>
                                            <ul class="c-margin-left--single c-margin-top--half">
                                                <li>
                                                    If an event has more than one occurrence, either
                                                    <code>REPEATING_RULE</code> or <code>ADHOC_DATES</code> must be
                                                    provided
                                                </li>
                                                <li>
                                                    Contact fields can use email addresses (system will resolve to
                                                    contact IDs)
                                                </li>
                                                <li>
                                                    Organization/Event Type fields can use names (system will resolve to
                                                    IDs)
                                                </li>
                                                <li>
                                                    Locations and Resources can use names (system will resolve to IDs).
                                                    For objects with numeric names, prepend with <code>$NAME-</code>
                                                </li>
                                            </ul>
                                        </div>
                                        <div class="c-margin-bottom--single">
                                            <s25-ng-import-custom-attribute-types />
                                        </div>
                                    </div>
                                </details>
                            </div>
                        }
                        @if (type.value === "organizations") {
                            <s25-ng-import-organizations [type]="type" [parentCd]="cd"></s25-ng-import-organizations>

                            <div class="c-margin-bottom--single c-margin-top--single">
                                <details class="c-margin-bottom--single">
                                    <summary
                                        class="c-margin-bottom--single"
                                        style="cursor: pointer; font-weight: bold;"
                                    >
                                        Organization Import Instructions
                                    </summary>
                                    <div class="import-instructions c-margin-left--single c-margin-top--single">
                                        <div class="c-margin-bottom--single">
                                            <strong>Required Columns:</strong>
                                            <ul class="c-margin-left--single c-margin-top--half">
                                                <li>
                                                    <code>Organization Name</code> (string, max 40 chars) - Organization
                                                    name (or use <code>Organization ID</code> for existing
                                                    organizations)
                                                </li>
                                                <li><code>role_name</code> (string) - Required for memberships</li>
                                                <li>
                                                    <code>contact_email</code> (string, max 80 chars) - Required for
                                                    memberships
                                                </li>
                                                <li>
                                                    <code>contact_first_name</code> (string, max 40 chars) - Required
                                                    for memberships
                                                </li>
                                                <li>
                                                    <code>contact_family_name</code> (string, max 40 chars) - Required
                                                    for memberships
                                                </li>
                                            </ul>
                                        </div>
                                        <div class="c-margin-bottom--single">
                                            <strong>Optional Organization Columns:</strong>
                                            <ul class="c-margin-left--single c-margin-top--half">
                                                <li>
                                                    <code>Organization ID</code> (number, optional) - Use to update
                                                    existing organization; when provided with a different
                                                    <code>Organization Name</code>, the name will be updated
                                                </li>
                                                <li><code>Title</code> (string, max 80 chars)</li>
                                                <li><code>Account Number</code> (string)</li>
                                                <li><code>contact_name</code> (string)</li>
                                                <li><code>S25 Name</code> (string)</li>
                                                <li>
                                                    <code>Type</code> (string|number) - Organization type name or ID
                                                    (numeric names should use <code>$NAME-</code> prefix, e.g.,
                                                    <code>$NAME-123</code>)
                                                </li>
                                                <li>
                                                    <code>Rating</code> (string|number) - Organization rating name or ID
                                                    (numeric names should use <code>$NAME-</code> prefix, e.g.,
                                                    <code>$NAME-123</code>)
                                                </li>
                                                <li>
                                                    <code>Category</code> (string) - Pipe-separated category names or
                                                    IDs (e.g., "Category1|Category2")
                                                </li>
                                            </ul>
                                        </div>
                                        <div class="c-margin-bottom--single">
                                            <strong>Address Columns (Optional):</strong>
                                            <ul class="c-margin-left--single c-margin-top--half">
                                                <li>
                                                    <strong>Administrative Address:</strong>
                                                    <ul class="c-margin-left--single c-margin-top--half">
                                                        <li>
                                                            <code>Administrative Street Address</code> (string, max 150
                                                            chars)
                                                        </li>
                                                        <li><code>Administrative City</code> (string, max 40 chars)</li>
                                                        <li>
                                                            <code>Administrative State / Province</code> (string, max 40
                                                            chars)
                                                        </li>
                                                        <li>
                                                            <code>Administrative Zip / Postal Code</code> (string, max
                                                            20 chars)
                                                        </li>
                                                        <li>
                                                            <code>Administrative Country</code> (string, max 40 chars)
                                                        </li>
                                                        <li>
                                                            <code>Administrative Phone</code> (string, max 24 chars)
                                                        </li>
                                                        <li><code>Administrative Fax</code> (string, max 24 chars)</li>
                                                    </ul>
                                                </li>
                                                <li>
                                                    <strong>Billing Address:</strong>
                                                    <ul class="c-margin-left--single c-margin-top--half">
                                                        <li>
                                                            <code>Billing Street Address</code> (string, max 150 chars)
                                                        </li>
                                                        <li><code>Billing City</code> (string, max 40 chars)</li>
                                                        <li>
                                                            <code>Billing State / Province</code> (string, max 40 chars)
                                                        </li>
                                                        <li>
                                                            <code>Billing Zip / Postal Code</code> (string, max 20
                                                            chars)
                                                        </li>
                                                        <li><code>Billing Country</code> (string, max 40 chars)</li>
                                                        <li><code>Billing Phone</code> (string, max 24 chars)</li>
                                                        <li><code>Billing Fax</code> (string, max 24 chars)</li>
                                                    </ul>
                                                </li>
                                            </ul>
                                        </div>
                                        <div class="c-margin-bottom--single">
                                            <strong>Notes:</strong>
                                            <ul class="c-margin-left--single c-margin-top--half">
                                                <li>
                                                    Column names are case-insensitive and support variations (e.g.,
                                                    "Organization ID", "organization id", or "OrganizationID" all work)
                                                </li>
                                                <li>
                                                    Address fields are optional - you can include Administrative,
                                                    Billing, both, or neither
                                                </li>
                                                <li>
                                                    Organization type and category fields can use names (system will
                                                    resolve to IDs)
                                                </li>
                                            </ul>
                                        </div>
                                        <div class="c-margin-bottom--single">
                                            <strong>Custom Attribute Columns:</strong>
                                            <ul class="c-margin-left--single c-margin-top--half">
                                                <li>
                                                    Prefix the header with <code>CA:</code> to import an organization
                                                    custom attribute (one attribute per column).
                                                </li>
                                                <li>
                                                    <code>CA: &lt;TYPE&gt; Name</code> - Auto-creates the attribute with
                                                    the given type if it does not exist yet (e.g.
                                                    <code>CA: &lt;B&gt; Active</code>,
                                                    <code>CA: &lt;S&gt; Department</code>,
                                                    <code>CA: &lt;N&gt; Capacity</code>)
                                                </li>
                                                <li>
                                                    <code>CA: Name</code> - Uses an existing attribute by name (no type
                                                    needed)
                                                </li>
                                                <li>
                                                    When multiple rows share the same organization (e.g. different
                                                    members), CA values must be identical across those rows. Conflicting
                                                    values for the same attribute will be rejected.
                                                </li>
                                            </ul>
                                            <s25-ng-import-custom-attribute-types />
                                        </div>
                                    </div>
                                </details>
                            </div>
                        }
                        @if (type.value === "contacts") {
                            <s25-ng-import-contacts [type]="type" [parentCd]="cd"></s25-ng-import-contacts>

                            <div class="c-margin-bottom--single c-margin-top--single">
                                <details class="c-margin-bottom--single">
                                    <summary
                                        class="c-margin-bottom--single"
                                        style="cursor: pointer; font-weight: bold;"
                                    >
                                        Contact Import Instructions
                                    </summary>
                                    <div class="import-instructions c-margin-left--single c-margin-top--single">
                                        <div class="c-margin-bottom--single">
                                            Use a single sheet (no separate tabs). Each row is one person. Include only
                                            the columns you need.
                                        </div>
                                        <div class="c-margin-bottom--single">
                                            <strong>Contact identity</strong> — use one approach per row:
                                            <ul class="c-margin-left--single c-margin-top--half">
                                                <li>
                                                    <code>Email</code>, <code>First Name</code>,
                                                    <code>Last Name</code> (string) — find or create the contact
                                                </li>
                                                <li>
                                                    <code>Contact ID</code> (number) — update an existing contact
                                                    directly; names not required
                                                </li>
                                            </ul>
                                        </div>
                                        <div class="c-margin-bottom--single">
                                            <strong>Other contact fields</strong> (optional):
                                            <ul class="c-margin-left--single c-margin-top--half">
                                                <li>
                                                    <code>Middle Name</code>, <code>Name Prefix</code>,
                                                    <code>Name Suffix</code>, <code>Title</code> (string)
                                                </li>
                                                <li>
                                                    <code>Address</code>, <code>City</code>, <code>State</code>,
                                                    <code>Zip</code>, <code>Country</code>, <code>Phone</code>,
                                                    <code>Fax</code> (string)
                                                </li>
                                                <li>
                                                    <code>Internal ID</code> (string) — SIS identifier; must be unique
                                                    within the upload
                                                </li>
                                            </ul>
                                        </div>
                                        <div class="c-margin-bottom--single">
                                            <strong>User</strong> (optional — omit to skip users):
                                            <ul class="c-margin-left--single c-margin-top--half">
                                                <li>
                                                    <code>Username</code> (string) — required when creating or updating
                                                    a user
                                                </li>
                                                <li>
                                                    <code>Password</code> (string) — optional; encrypted on save. Leave
                                                    blank to keep an existing password (new users get a random one)
                                                </li>
                                                <li>
                                                    <code>Security Group</code> (string) — optional; must already exist
                                                    (matched by name). Requires <code>Username</code>
                                                </li>
                                            </ul>
                                        </div>
                                        <div class="c-margin-bottom--single">
                                            <strong>Student Engagement:</strong> Use the checkbox above to mark every
                                            imported contact and user as a Student Engagement record.
                                        </div>
                                        <div class="c-margin-bottom--single">
                                            <strong>Custom Attribute Columns:</strong>
                                            <ul class="c-margin-left--single c-margin-top--half">
                                                <li>
                                                    Prefix the header with <code>CA:</code> to import a contact custom
                                                    attribute (one attribute per column).
                                                </li>
                                                <li>
                                                    <code>CA: &lt;TYPE&gt; Name</code> - Auto-creates the attribute with
                                                    the given type if it does not exist yet (e.g.
                                                    <code>CA: &lt;B&gt; Veteran</code>,
                                                    <code>CA: &lt;S&gt; Major</code>,
                                                    <code>CA: &lt;N&gt; Credits</code>)
                                                </li>
                                                <li>
                                                    <code>CA: Name</code> - Uses an existing attribute by name (no type
                                                    needed)
                                                </li>
                                            </ul>
                                            <s25-ng-import-custom-attribute-types />
                                        </div>
                                    </div>
                                </details>
                            </div>
                        }

                        @if (type.value === "budgets") {
                            <s25-ng-import-budgets [type]="type" [parentCd]="cd"></s25-ng-import-budgets>
                        }

                        @if (type.value === "masterDefinitions") {
                            <s25-ng-import-master-definitions
                                [type]="type"
                                [parentCd]="cd"
                            ></s25-ng-import-master-definitions>

                            <div class="c-margin-bottom--single c-margin-top--single">
                                <details class="c-margin-bottom--single">
                                    <summary
                                        class="c-margin-bottom--single"
                                        style="cursor: pointer; font-weight: bold;"
                                    >
                                        Master Definition Import Instructions
                                    </summary>
                                    <div class="import-instructions c-margin-left--single c-margin-top--single">
                                        <div class="c-margin-bottom--single">
                                            <strong>Required:</strong> <code>Name</code>. Event types need
                                            <code>Node Type</code> and a parent folder type (<code>Parent</code>).
                                            Custom attributes also need <code>Type</code> as a single-character code.
                                        </div>
                                        <div class="c-margin-bottom--single">
                                            <s25-ng-import-custom-attribute-types />
                                        </div>
                                        <div class="c-margin-bottom--single">
                                            Numeric values are existing IDs and must already exist. For a name that is
                                            only digits, use <code>$NAME-123</code>. Missing names are created.
                                        </div>
                                        <div class="c-margin-bottom--single">
                                            Abridged status is set on the Master Definitions page after import.
                                        </div>
                                    </div>
                                </details>
                            </div>
                        }

                        @if (type.errors?.length) {
                            <div class="ngRed c-margin-bottom--single">Validation Errors</div>
                            <ul
                                [style.border-left]="'3px solid #dc3545'"
                                class="c-margin-left--single c-margin-bottom--single"
                            >
                                @for (error of type.errors; track $index) {
                                    <li class="c-margin-bottom--single">{{ error }}</li>
                                }
                            </ul>
                            @if (type.conflicts?.spConflicts?.length) {
                                <div class="c-margin-bottom--single">
                                    <span class="ngRed">Space Conflicts</span>
                                    <ul class="c-margin-left--single c-margin-bottom--single">
                                        @for (conflict of type.conflicts.spConflicts; track $index) {
                                            <li class="c-margin-bottom--single">
                                                <div>{{ conflict.conflictTypeName }} in {{ conflict.roomName }}</div>
                                                <div>
                                                    Candidate:
                                                    {{
                                                        conflict.candidateEventName
                                                            ? conflict.candidateEventName +
                                                              " (" +
                                                              conflict.candidateDateTimeStr +
                                                              ")"
                                                            : conflict.candidateDateTimeStr
                                                    }}
                                                </div>
                                                <div>
                                                    Conflict:
                                                    {{
                                                        conflict.conflictEventName
                                                            ? conflict.conflictEventName +
                                                              " (" +
                                                              conflict.conflictDateTimeStr +
                                                              ")"
                                                            : conflict.conflictDateTimeStr
                                                    }}
                                                </div>
                                            </li>
                                        }
                                    </ul>
                                </div>
                            }
                            @if (type.conflicts?.rsConflicts?.length) {
                                <div class="c-margin-bottom--single">
                                    <span class="ngRed">Resource Conflicts</span>
                                    <ul class="c-margin-left--single c-margin-bottom--single">
                                        @for (conflict of type.conflicts.rsConflicts; track $index) {
                                            <li class="c-margin-bottom--single">
                                                <div>
                                                    {{ conflict.conflictTypeName }} in {{ conflict.resourceName }}
                                                </div>
                                                @if (conflict.conflictType === "stock") {
                                                    <div>
                                                        Inadequate stock available. Over by {{ conflict.overBy ?? 0 }}.
                                                    </div>
                                                }
                                                <div>
                                                    Candidate: {{ conflict.candidateEventName }} ({{
                                                        conflict.candidateDateTimeStr
                                                    }})
                                                </div>
                                                @if (conflict.conflictType !== "stock") {
                                                    <div>
                                                        Conflict:
                                                        {{
                                                            conflict.conflictEventName
                                                                ? conflict.conflictEventName +
                                                                  "(" +
                                                                  conflict.conflictDateTimeStr +
                                                                  ")"
                                                                : conflict.conflictDateTimeStr
                                                        }}
                                                    </div>
                                                    @if (conflict.overBy != null) {
                                                        <div>Over by {{ conflict.overBy }}</div>
                                                    }
                                                }
                                            </li>
                                        }
                                    </ul>
                                </div>
                            }
                        }

                        @if (type.successMessage) {
                            <div class="ngGreen c-margin-bottom--single">
                                {{ type.successMessage }}
                            </div>
                        }

                        <s25-ng-spreadsheet
                            class="c-margin-top--single"
                            [onComplete]="type.onComplete"
                            [completeText]="type.completeText"
                            [widthFunc]="widthFunc"
                        ></s25-ng-spreadsheet>
                    </div>
                }
            }
        }
    `,
    styles: [
        `
            .import-instructions strong {
                margin-inline-end: 0.35em;
            }
        `,
    ],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImportComponent implements OnInit, OnDestroy {
    init = false;
    private readonly productLicenseStore = inject(ProductLicenseStore);
    private readonly telemetrySession: TelemetrySession = ImportTelemetry.createSession("Import");
    importTypes: ImportType[] = [
        {
            name: "Events",
            value: "events",
            completeText: "Import Events",
            shown: false,
            init: false,
            errors: [],
            conflicts: undefined,
            successMessage: "",
            onComplete: undefined, // defined in import.events.component.ts
        },
        {
            name: "Organizations",
            value: "organizations",
            completeText: "Import Organizations",
            shown: false,
            init: false,
            errors: [],
            conflicts: undefined,
            successMessage: "",
            onComplete: undefined, // defined in import.organizations.component.ts
        },
        {
            name: "Contacts",
            value: "contacts",
            completeText: "Import Contacts",
            shown: false,
            init: false,
            errors: [],
            conflicts: undefined,
            successMessage: "",
            onComplete: undefined, // defined in import.contacts.component.ts
        },
        {
            name: "Master Definitions",
            value: "masterDefinitions",
            completeText: "Import Master Definitions",
            shown: false,
            init: false,
            errors: [],
            conflicts: undefined,
            successMessage: "",
            onComplete: undefined,
        },
        {
            name: "Budgets",
            value: "budgets",
            completeText: "Import Budgets",
            shown: false,
            init: false,
            errors: [],
            conflicts: undefined,
            successMessage: "",
            onComplete: undefined,
        },
    ];
    selectedType: ImportType | undefined = undefined;
    widthFunc: () => number = () => Math.floor(window.innerWidth * 0.9);

    constructor(public cd: ChangeDetectorRef) {}

    get visibleImportTypes(): ImportType[] {
        if (this.productLicenseStore.hasStudentEngagement()) {
            return this.importTypes;
        }
        return this.importTypes.filter((importType) => importType.value !== "budgets");
    }

    onSelectedTypeChange(type: ImportType) {
        this.importTypes.forEach((t) => (t.shown = false));
        type.shown = true;
        type.init = true;
        this.cd.detectChanges();
    }

    async ngOnInit() {
        this.telemetrySession.open();
        await this.productLicenseStore.ensureLoaded();
        this.selectedType = this.visibleImportTypes[0];
        this.onSelectedTypeChange(this.selectedType);
        await SpreadSheetService.load();
        this.init = true;
        this.cd.detectChanges();
    }

    ngOnDestroy() {
        this.telemetrySession.close();
    }
}
